import { ArrowUpRight } from 'lucide-react';

import { cn } from '@/shared/lib/cn';
import { Spinner } from '@/shared/ui/spinner';

import type { ReactNode } from 'react';

export function FieldEmpty({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="field-empty">
      <h3 className="text-sm font-medium">{title}</h3>
      <p className="text-fg-muted mt-2 max-w-md text-sm leading-6">{children}</p>
    </div>
  );
}

export function FieldStatus({ value }: { value: string }) {
  return (
    <span
      className={cn(
        'field-status',
        ['failed', 'blocked', 'changes_required', 'changes_requested', 'cancelled'].includes(value)
          ? 'text-danger'
          : ['passed', 'ready', 'approved', 'completed'].includes(value)
            ? 'text-success'
            : 'text-fg-muted',
      )}
    >
      {['pending', 'queued', 'running', 'generating', 'processing'].includes(value) ? (
        <Spinner className="size-3" />
      ) : null}
      {value.charAt(0).toUpperCase() + value.slice(1).replaceAll('_', ' ')}
    </span>
  );
}

export function FieldPanelHeading({
  title,
  children,
  action,
  tourTarget,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  tourTarget?: string;
}) {
  return (
    <div className="field-panel-heading" data-field-tour={tourTarget}>
      <div>
        <h2>{title}</h2>
        {children ? <p>{children}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function FieldExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="text-link">
      {children}
      <ArrowUpRight className="size-4" aria-hidden="true" />
    </a>
  );
}

export function FieldTime({ value }: { value: string }) {
  const date = new Date(value);
  return (
    <time dateTime={value}>
      {Number.isNaN(date.getTime())
        ? value
        : date.toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
    </time>
  );
}
